<template>
  <span
    v-if="inline"
    class="ws-message inline"
    :class="type"
  >
    <template v-if="hasMessages">
      <span
        v-for="(msg, index) in msgs"
        :key="texts[index] + index"
      >
        <span v-if="index">, </span>{{ texts[index] }}</span>
    </template>
    <template v-else>
      <slot></slot>
    </template>
  </span>
  <div
    v-else-if="hasMessages"
    class="ws-message"
    :class="{
      banner,
      prepend: $slots.prepend || icon,
      append: $slots.append || button,
      [type]: true,
    }"
  >
    <template v-for="(msg, index) in msgs">
      <ws-state-banner
        v-if="banner"
        :key="texts[index] + index"
        v-bind="$attrs"
        :type="type"
      >
        <div
          v-if="$slots.prepend || iconResolved"
          class="prepend"
        >
          <slot name="prepend">
            <ws-icon
              :name="iconResolved"
              v-bind="iconAttrsResolved"
            />
          </slot>
        </div>
        <div class="content q-ml-md">
          <div
            v-if="$slots.title || texts[index]"
            class="title"
            :class="
              !$slots.title || descriptions[index]
                ? 'text-bold text-' + (featuresColor || 'bold')
                : ''
            "
          >
            <slot name="title">
              <small><span v-if="prepend">{{ $t(prepend) }} </span>{{ texts[index] }}</small>
            </slot>
          </div>
          <slot name="content">
            <small v-if="descriptions[index]">{{ descriptions[index] }}</small>
          </slot>
        </div>
        <div
          v-if="$slots.append || button"
          class="append q-ml-lg"
        >
          <slot name="append">
            <ws-button
              :class="{
                'default-button-margins': !get(
                  buttonAttrs,
                  'clearDefaultButtonMargins'
                ),
              }"
              v-bind="buttonAttrsResolved"
            >
              {{
                $t(button.translationKey || 'uikit:general.link', button.values)
              }}
            </ws-button>
          </slot>
        </div>
        <!-- TODO: Ask Peto Posa about this feature -->
        <!-- <template v-if="canClose">
            <ws-button
              flat
              color="ws-error"
              icon="close"
              @click="remove(index, msg)"
            />
          </template> -->
      </ws-state-banner>
      <q-banner
        v-else
        :key="texts[index] + index"
      >
        <span v-if="prepend">{{ $t(prepend) }} </span>{{ texts[index] }}
      </q-banner>
    </template>
  </div>
  <div
    v-else-if="$slots.default"
    class="ws-message"
    :class="{
      banner,
      inline,
      prepend: $slots.prepend || icon,
      append: $slots.append || button,
      [type]: true,
    }"
  >
    <slot></slot>
  </div>
</template>

<script lang="ts">
import {Component, Vue, Prop, Watch} from 'vue-property-decorator';
import {QBanner} from 'quasar';

import {UI_VARIANT} from './const.enum';
import {isArray} from 'lodash-es';
import { WsButton, WsIcon } from '@odin/components-vue2';
import WsStateBanner from './WsStateBanner.vue';
import { type Message, type Subscription, useI18n, useMessages } from '@odin/services';

const {subscribeMessage, deleteMessage} = useMessages();

@Component({
  components: {
    QBanner,
    WsButton,
    WsIcon,
    WsStateBanner,
  },
})
export class WsMessage extends Vue {
  @Prop({default: undefined}) readonly path!: string | undefined;
  @Prop({default: null}) readonly messages!:
    | Message[]
    | string[]
    | string
    | null;
  @Prop({default: ''}) readonly prepend!: string; // prepend message text
  @Prop({default: ''}) readonly description!: string;
  @Prop(Boolean) readonly recursive!: boolean;
  @Prop(Boolean) readonly fallback!: boolean;

  // features
  @Prop({type: String, default: UI_VARIANT.DEFAULT})
  readonly type!: UI_VARIANT;
  @Prop([Boolean, String]) readonly icon!: boolean | string;
  @Prop({default: () => ({})}) readonly iconAttrs!: Object; // WsIcon attributes
  @Prop() readonly button!: {
    translationKey: string;
    values?: any;
    action: Function | string;
  }; // function -> callback, string -> url
  @Prop({default: () => ({})}) readonly buttonAttrs!: Object; // WsButton attributes, (i) can be also "class"!
  // @Prop(Boolean) readonly canClose!: boolean;

  // versions
  @Prop(Boolean) readonly inline!: boolean; // inline
  @Prop(Boolean) readonly banner!: boolean; // ws-state-banner

  i18n = useI18n();
  errorMessages: Message[] = [];
  messageSubscription: Subscription | null;

  handleMessage(message: Message) {
    message
      ? this.errorMessages.push(message)
      : this.errorMessages = [];
  }

  get msgs(): Message[] {
    const messages =
      typeof this.messages === 'string' && this.messages
        ? [{value: this.$t(this.messages)}]
        : this.messages;

    const noMessagesCase =
      this.$slots.content || this.$slots.title ? ([{}] as any) : [];

    return isArray(messages)
      ? (messages as (Message | string)[]).map(msg =>
        typeof msg === 'string' ? {value: this.$t(msg)} : msg
      )
      : (this.path && this.errorMessages) || noMessagesCase;
  }

  get hasMessages() {
    return !!this.msgs?.length;
  }

  get texts() {
    return this.msgs.map(
      (msg: any) =>
        msg.value || msg.payload?.text || this.$t(msg.value, msg.values)
    );
  }

  get descriptions() {
    return this.msgs.map((msg: any) => {
      if(this.i18n.i18next.exists(msg.translationKey + '_description')) {
        const keys = this.description
          ? [msg.translationKey + '_description', this.description]
          : msg.translationKey + '_description';
        return this.$t(keys);
      }

      return '';
    }
    );
  }

  get iconAttrsResolved() {
    return {size: 22, color: this.featuresColor, ...this.iconAttrs};
  }

  get buttonAttrsResolved() {
    return {
      flat: true,
      small: true,
      noWrap: true,
      icon: 'external_link',
      color: this.featuresColor,
      ...this.buttonAttrs,
    };
  }

  get iconResolved(): string | undefined {
    const icon = this.icon;
    if (!icon) {
      return undefined;
    } else if (typeof icon === 'string') {
      return icon;
    }
    return {
      [UI_VARIANT.DEFAULT]: 'info',
      [UI_VARIANT.SUCCESS]: 'success',
      [UI_VARIANT.ALERT]: 'error',
      [UI_VARIANT.NEUTRAL]: 'question',
    }[this.type];
  }

  get featuresColor(): string | undefined {
    return {
      [UI_VARIANT.DEFAULT]: undefined,
      [UI_VARIANT.SUCCESS]: 'ws-success',
      [UI_VARIANT.ALERT]: 'ws-error',
      [UI_VARIANT.NEUTRAL]: 'ws-info',
    }[this.type];
  }

  @Watch('path', {immediate: true})
  onPathChanged(path: string, oldPath: string) {
    if (oldPath) {
      this.messageSubscription?.unsubscribe();
    }

    if (path) {
      this.messageSubscription = subscribeMessage(this.path, this.handleMessage);
    }
  }

  remove(index: number, msg: Message) {
    if (this.path) {
      deleteMessage(this.path);
    }
    this.$emit('remove', index, msg);
  }

  destroyed() {
    if (this.path) {
      this.messageSubscription?.unsubscribe();
    }
  }
}

export default WsMessage;
</script>

<style lang="scss" scoped>
.ws-message {
  > .ws-state-banner {
    display: grid;
    grid-template-areas: 'prepend content append';
    grid-template-columns: min-content auto min-content;

    .prepend {
      grid-area: prepend;
    }

    .content {
      grid-area: content;
      place-self: center start;
      color: $ws-text-color-dark;
    }

    .append {
      grid-area: append;
      align-self: end;

      > .ws-button.default-button-margins {
        margin-right: -(map-get($space-md, x));
        margin-bottom: -(map-get($space-sm, y));
      }
    }

    & + .ws-state-banner {
      margin-top: map-get($space-md, y);
    }
  }

  > .q-banner {
    padding: 0;
    min-height: 14px;
    background: transparent;

    & + .q-banner {
      margin-top: map-get($space-sm, y);
    }
  }

  &.inline {
    display: inline;
  }

  &.error, // .error is deprecated, use type="alert" instead
  &.alert {
    &.inline,
    > .q-banner,
    > .ws-state-banner .content ::v-deep a.ws-message-ft-color {
      color: $ws-error;
    }
  }

  &.success {
    &.inline,
    > .q-banner,
    > .ws-state-banner .content ::v-deep a.ws-message-ft-color {
      color: $ws-success;
    }
  }

  &.neutral {
    &.inline,
    > .q-banner,
    > .ws-state-banner .content ::v-deep a.ws-message-ft-color {
      color: $ws-info;
    }
  }
}
</style>
